/*
 * Copyright 2026 Sony Corporation
 */
import type { Device, Project, Run, Version } from '@ips-jit/schema';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { DeviceScreen } from '../components/DeviceScreen';
import { GeneratedApps } from '../components/GeneratedApps';
import { ProjectChat } from '../components/ProjectChat';
import { SettingsPanel } from '../components/SettingsPanel';
import { TodoPanel } from '../components/TodoPanel';
import { useAuth } from '../contexts/auth-context';
import { lastDevice, pickDevice } from '../services/device-choice';
import { todoItems } from '../services/todo';

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

const FAST_REFRESH_MS = 4_000;
const IDLE_REFRESH_MS = 20_000;
/** The device's agent reports changes at once, so the page need not ask often to show them soon. */
const DEVICE_REFRESH_MS = 10_000;

/** A version is still moving until its build has ended and the device has answered. */
const inFlight = (v: Version): boolean =>
  v.status === 'building' ||
  (v.status === 'built' && (v.deployment_state === 'sent' || v.deployment_state === 'downloading'));

/** The version a request is building or has built: the one it names, else the newest made since it started. */
const versionOf = (run: Run | null, versions: Version[]): Version | undefined =>
  run
    ? ((run.version !== null ? versions.find((v) => v.version === run.version) : undefined) ??
      versions.find((v) => Date.parse(v.created_at) >= Date.parse(run.created_at)))
    : undefined;

/** The project on the left, the conversation with the device log in the middle, progress and the generated apps right. */
export const ProjectDetail = () => {
  const { api } = useAuth();
  const { projectId } = useParams();
  const [project, setProject] = useState<Project>();
  const [devices, setDevices] = useState<Device[]>([]);
  const [versions, setVersions] = useState<Version[]>([]);
  const [deviceId, setDeviceId] = useState('');
  const [projectOpen, setProjectOpen] = useState(true);
  const [progress, setProgress] = useState<{ run: Run | null; step?: string }>({ run: null });
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!projectId) {
      return;
    }
    api.getProject(projectId).then(setProject, (caught: unknown) => {
      setError(describe(caught, 'Could not load the project.'));
    });
  }, [api, projectId]);

  useEffect(() => {
    const load = () => api.listDevices().then(setDevices, () => undefined);
    void load();
    const timer = setInterval(() => void load(), DEVICE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [api]);

  // The project's own runtime; for projects from before runtimes were chosen, the device used last, else the online
  // one seen most recently, and the user can still pick another.
  useEffect(() => {
    if (project?.runtime_id) {
      setDeviceId(project.runtime_id);
    } else if (projectId && deviceId === '' && devices.length > 0) {
      setDeviceId(pickDevice(devices, lastDevice(projectId)));
    }
  }, [devices, deviceId, projectId, project?.runtime_id]);

  const loadVersions = useCallback(async () => {
    if (!projectId) {
      return;
    }
    try {
      setVersions(await api.listVersions(projectId));
    } catch (caught) {
      setError(describe(caught, 'Could not load builds.'));
    }
  }, [api, projectId]);

  const watching = versions.some(inFlight);
  useEffect(() => {
    void loadVersions();
    const timer = setInterval(() => void loadVersions(), watching ? FAST_REFRESH_MS : IDLE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadVersions, watching]);

  const deployAgain = async (build: number) => {
    if (!projectId) {
      return;
    }
    setError(undefined);
    try {
      await api.deployVersion(projectId, build);
      await loadVersions();
    } catch (caught) {
      setError(describe(caught, 'Could not send the build.'));
    }
  };

  const todos = useMemo(
    () => todoItems(progress.run, progress.step, versionOf(progress.run, versions)),
    [progress, versions]
  );

  if (!project) {
    return error ? (
      <Stack spacing={2}>
        <Alert severity="error">{error}</Alert>
        <Button component={RouterLink} to="/projects" sx={{ alignSelf: 'flex-start' }}>
          Back to projects
        </Button>
      </Stack>
    ) : (
      <Typography color="text.secondary">Loading...</Typography>
    );
  }

  return (
    <Stack spacing={2} sx={{ height: { md: '100%' } }}>
      {error && <Alert severity="error">{error}</Alert>}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: `${projectOpen ? '340px' : '48px'} minmax(0, 1fr) 340px` },
          gridTemplateRows: { md: 'minmax(0, 1fr)' },
        }}
      >
        <SettingsPanel
          // The builds list is polled while something is moving; the project record is read once, so it would lag.
          project={{
            ...project,
            current_version: Math.max(project.current_version, ...versions.map((v) => v.version ?? 0)),
          }}
          devices={devices}
          deviceId={deviceId}
          onDeviceChange={setDeviceId}
          open={projectOpen}
          onToggle={() => setProjectOpen(!projectOpen)}
        >
          <GeneratedApps
            projectId={project.project_id}
            versions={versions}
            devices={devices}
            onDeployAgain={(v) => void deployAgain(v)}
          />
        </SettingsPanel>
        <ProjectChat projectId={project.project_id} deviceId={deviceId} errorsOnly onProgress={setProgress} />
        {/* The panels keep their own height; the column scrolls when together they are taller than the window. */}
        <Stack spacing={2} sx={{ minHeight: 0, overflowY: { md: 'auto' } }}>
          <TodoPanel items={todos} />
          <DeviceScreen
            projectId={project.project_id}
            running={versions.some((v) => v.version !== null && v.deployment_state === 'applied')}
          />
        </Stack>
      </Box>
    </Stack>
  );
};
